import { PayloadAction } from '@reduxjs/toolkit';
import { all, call, fork, put, select, takeEvery } from 'redux-saga/effects';
import { IVideoState, loadingState, updatePlayerState, updateSceneState, updateVideoState } from 'core/slices/player';
import { selectAction } from 'core/selectors/product/action/action';
import { IActionBasic } from 'types/domain/action';
import { RegisterState, actionSubmit, next, registerActionState, updateRendered } from 'core/slices/register';
import { IToken, Mode, TokenErrorStatus } from 'core/slices/session';
import { selectSessionMode } from 'core/selectors/admin/sessionMode';
import { RootState } from 'types';
import { EntityTypes } from 'constants/entities';
import { selectShouldPause } from 'core/selectors/product/shouldPause';
import { selectFeedNext } from 'core/selectors/product/action/actionNext';
import { LoadingState } from 'types/domain/base';
import { IActionFeed, IActionFeedItem, selectActionFeed } from 'core/selectors/product/action/actionFeed';
import { selectActionEditor } from 'core/slices/actionEditor/selectActionEditor';
import { IActionEditorState } from 'core/slices/actionEditor';
import { selectActionRegister } from 'core/selectors/product/action/actionRegister';
import { authRequest } from 'core/services/authentication';
import { tokenRequest } from 'core/services/product';
import { selectToken } from 'core/selectors/product/token';

function* videoStateSaga(action: PayloadAction<IVideoState>) {
  const sceneId = yield select((state: RootState) => state.state[EntityTypes.SCENE]);
  if (action.payload.ready) {
    yield all([
      put(yield call(loadingState, { scene: LoadingState.LOADED })),
      put(yield call(updateRendered, { type: EntityTypes.SCENE, rendered: true, id: sceneId })),
      put(
        yield call(updateSceneState, {
          id: sceneId,
          load: LoadingState.LOADED,
          ended: false,
          started: true,
        }),
      ),
    ]);
  }

  const mode: Mode = yield select(selectSessionMode);

  if (action.payload.ended === true) {
    const paused = yield select((state: RootState) => state.core.player.state.paused);

    const actionEditor: IActionEditorState = yield select(selectActionEditor);

    if (actionEditor?.enabled) return yield put(yield call(updatePlayerState, { paused: true, cuprit: 2.2 }));

    const actionId = yield select((state: RootState) => state.state[EntityTypes.ACTION]);
    const sessionAttemptId = yield select((state: RootState) => state.state[EntityTypes.SESSION_ATTEMPT]);

    if (mode !== Mode.ADMIN && actionId && sessionAttemptId)
      yield put(
        yield call(registerActionState, {
          actionId,
          sessionAttemptId,
          state: RegisterState.ENDED,
        }),
      );

    const shouldPause = yield select(selectShouldPause);

    if (shouldPause && !paused) yield put(yield call(updatePlayerState, { paused: true, cuprit: 2.3 }));

    if (mode === Mode.ADMIN) return;

    const actionFeed: IActionFeed = yield select(selectActionFeed);
    const nextItem: IActionFeedItem = yield select(selectFeedNext);

    const nextAction: IActionBasic = yield select(state =>
      //@ts-ignore
      nextItem?.actionId ? selectAction(state, nextItem.actionId) : null,
    );
    const actionRegister = yield select(selectActionRegister);
    if (actionRegister && !actionRegister?.result)
      yield put(yield call(actionSubmit, { actionId: actionRegister.actionId }));
    else {
      yield put(yield call(next, { auto: actionFeed.index === actionFeed.feed.length - 1, culprit: 'videoStateSaga' }));
    }

    if (nextItem) {
      if (nextAction && nextAction.sceneId !== sceneId)
        yield put(
          yield call(updateSceneState, {
            id: sceneId,
            ended: true,
          }),
        );
    }
  }
}

function* authSaga() {
  const token: IToken = yield select(selectToken);

  if (token?.status === TokenErrorStatus.PRIVATE) yield put(yield call(tokenRequest, { token: token.token }));
}

function* watchAuth() {
  yield takeEvery(authRequest.fulfilled, authSaga);
}

function* watchUpdateVideo() {
  yield takeEvery(updateVideoState, videoStateSaga);
}

function* watchUpdateControlsState() {
  //yield takeEvery(updateControlsState, controlsStateSaga);
}

export function* eventsSaga() {
  yield all([fork(watchUpdateVideo), fork(watchUpdateControlsState), fork(watchAuth)]);
}
